import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { sharedApi } from "./api";
import { AllAccountsFilterParams, ConsumerPlanConfigParams, ConsumerDetailParams, ConsumerBillParams, UtilityConfigFilters, AddNotePayload, MultipleConsumerBillsParams } from "./types";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";

export const useAllAccounts = (filters: AllAccountsFilterParams) => {
  return useSmartQuery(
    ["cx", "allAccounts", "list", filters],
    () => sharedApi.getAllAccounts(filters),
    {
      enabled: !!filters.remote_utility_id && !!filters.consumer_id,
    }
  );
};

export const useConsumerPlanConfig = (params: ConsumerPlanConfigParams) => {
  return useSmartQuery(
    ["cx", "consumerPlanConfig", "detail", params],
    () => sharedApi.getConsumerPlanConfig(params),
    {
      enabled: !!params.remote_utility_id && !!params.consumer_id,
    }
  );
};

export const useConsumerDetail = (
  params: ConsumerDetailParams,
  options?: { enabled?: boolean }
) => {
  return useSmartQuery(
    ["consumerWeb", "consumerDetail", params],
    () => sharedApi.getConsumerDetail(params),
    {
      enabled: !!params.remote_utility_id,
      refetchInterval: false,
      ...options,
    }
  );
};

export const useConsumerBill = (params: ConsumerBillParams) => {
  return useSmartQuery(
    ["consumerWeb", "consumerBill", params],
    () => sharedApi.getConsumerBill(params),
    {
      enabled: !!params.remote_utility_id && !!params.consumer_number,
    }
  );
};

export const useMultipleConsumerBills = (params: MultipleConsumerBillsParams) => {
  return useSmartQuery(
    ["consumerWeb", "multipleConsumerBills", params],
    () => sharedApi.getMultipleConsumerBills(params),
    {
      enabled: !!params.remote_utility_id && !!params.consumer_no && (Array.isArray(params.consumer_no) ? params.consumer_no.length > 0 : true),
    }
  );
};

export const useUtilityRequestConfiguration = (
 filters: UtilityConfigFilters
) => {
 return useSmartQuery(
  QueryKeyFactory.module.cx.disconnection.utilityConfig(filters),
  () => sharedApi.getUtilityRequestConfiguration(filters),
  {
   enabled: !!filters.requestType,
  }
 );
};

export const useTimeSlotChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnection.timeSlotChoices(),
    () => sharedApi.getTimeSlotChoices()
  );
};

export const useActivityLog = (params: {
  id: string;
  remote_utility_id: string;
  module:any
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.request.activityLog(params),
    () => sharedApi.getActivityLog(params)
  );
};

export const useNotes = (params: {
  remote_utility_id: string;
  request_id:any;
  source:string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.request.Notes(params),
    () => sharedApi.getNotes(params)
  );
};

export const useRequestDetail = (params: {
  remote_utility_id: string;
  id:string
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.request.requestDetail(params),
    () => sharedApi.getRequestDetail(params)
  );
};

export const useAddNote = () => {
  return useSmartMutation(
    (payload: AddNotePayload ) =>
      sharedApi.addNote(payload)
  );
};

export const usePaymentList = (params: {
  remote_utility_id?: string | number;
  consumer_id?: string | number | any[];
  page?: number | string;
  limit?: number | string;
}) => {
  return useSmartQuery(
    ["consumerWeb", "paymentList", params],
    () => sharedApi.getPaymentList(params),
    {
      enabled: !!params.remote_utility_id && !!params.consumer_id,
    }
  );
};

export const useDownloadBillTemplate = () => {
  return useSmartMutation(
    (params: { billId: string }) => sharedApi.downloadBillTemplate(params),
    {
      onSuccess: (blob: any, variables: any) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentType = blob.type;
        let extension = "pdf";

        if (
          contentType.includes("excel") ||
          contentType.includes("spreadsheet")
        ) {
          extension = "xlsx";
        } else if (contentType.includes("csv")) {
          extension = "csv";
        } else if (contentType.includes("pdf")) {
          extension = "pdf";
        }

        link.download = `bill_${variables.billId}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
    }
  );
};

export const useViewBillTemplate = () => {
  return useSmartMutation(
    (params: { billId: string }) => sharedApi.downloadBillTemplate(params),
    {
      onSuccess: (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      },
    }
  );
};

export const useAccountStatusChoices = () => {
  return useSmartQuery(
    ["cx", "choices", "accountStatus"],
    () => sharedApi.getAccountStatusChoices()
  );
};
